import { Injectable } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { SubscriptionStatuses } from '@site-mate/sitemate-global-shared';

import { LocalStorageService } from './local-storage.service';
import { TeamService } from './team.service';
import { BannerBadgeTypes } from '../model/banner-badge-types.enum';
import { BannerKeys } from '../model/banner-keys.enum';
import { BannerMetadata } from '../model/banner-metadata.model';

@Injectable({ providedIn: 'root' })
export class BannerService {
  private unsub = new Subject();
  private subscriptionStatus: SubscriptionStatuses;
  private readonly dayInterval = 1000 * 60 * 60 * 24; // 1 day.
  private readonly releaseInterval = this.dayInterval * 90;

  constructor(
    private readonly teamService: TeamService,
    private readonly localStorageService: LocalStorageService,
  ) {
    this.teamService.currentTeam.pipe(takeUntil(this.unsub)).subscribe((team) => {
      this.subscriptionStatus = team.companyMetadata?.billing?.subscription?.status;
    });
  }

  isVisible(bannerKey: BannerKeys, isBadgeOnly = false): boolean {
    const isTrialSubscription = this.subscriptionStatus === SubscriptionStatuses.FreeTrial;

    if (BannerMetadata[bannerKey].type === BannerBadgeTypes.Tip) {
      return isTrialSubscription;
    }

    if (BannerMetadata[bannerKey].type === BannerBadgeTypes.New && isBadgeOnly) {
      return this.isNewAnnouncement(bannerKey);
    }

    if (BannerMetadata[bannerKey].type === BannerBadgeTypes.New) {
      return this.isNewAnnouncement(bannerKey) && !this.isBannerDismissed(bannerKey);
    }

    return false;
  }

  isNewAnnouncement(bannerKey: BannerKeys): boolean {
    const isPaidSubscription = this.subscriptionStatus === SubscriptionStatuses.Paid;
    return isPaidSubscription && !this.isExpired(bannerKey);
  }

  isExpired(bannerKey: BannerKeys): boolean {
    if (BannerMetadata[bannerKey].type === BannerBadgeTypes.Tip || !BannerMetadata[bannerKey].releaseDate) {
      return false;
    }

    const releaseDate = new Date(BannerMetadata[bannerKey].releaseDate);
    const newBannerExpireDate = new Date(releaseDate.getTime() + this.releaseInterval);

    return newBannerExpireDate <= new Date();
  }

  isBannerDismissed(bannerKey: BannerKeys): boolean {
    const fullStorageKey = `${BannerMetadata[bannerKey].localStorageKey}-visible`;
    return this.localStorageService.getItem(fullStorageKey) === 'false';
  }
}
